import React, { ReactNode, ChangeEvent, MouseEvent, useState, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import ListRow from './ListRow';
import './../../shared/Table/_Table.scss';
import './_List.scss';
import { Action, Row, SelectRowAction } from 'components/List/types';
import { Option } from 'shared/interface/globalInterface';
import { Field } from 'formik';
import { QuoteRadio } from 'shared/Inputs/Quote/QuoteRadio';
import { Nullable } from 'utils/types';
import Select from './../../shared/Inputs/Select/Select';

const Moment = require('moment');

enum SORT_OPTIONS {
  BY_DATE_SUBMITTED = 'DateSubmitted',
  BY_LAST_UPDATED = 'LastUpdated',
}

type Props = {
  labels: string[];
  icons?: ReactNode[];
  rows: Row[];
  onAction: Action;
  listName: string;
  subtitle?: string;
  addNewLabel?: string;
  onAddNewItem?: Action;
  search?: {
    isPerformed: boolean;
    placeholder: string;
    query: string;
    onSearch?: (event: ChangeEvent<HTMLInputElement>) => void;
  };
  hasGap?: boolean;
  isSmall?: boolean;
  selectable?: boolean;
  selectedRow?: Row;
  onRowSelect?: SelectRowAction;
  filters?: {
    options: Option[];
    filterBy: string;
    text: string;
  };
  isSortable?: boolean;
  isSorted?: boolean;
  filtersAndSearchComponents?: JSX.Element;
};

const filterBy = (rows: Row[], filter: string, search: string) => {
  const found = rows.filter((row) => {
    if (!row.filters) return row;
    const foundFilter = row.filters.find(([by, _]) => by === filter);

    return foundFilter && foundFilter[1] === search;
  });

  return found.length > 0 ? found : rows;
};

const sortRowsByLatestDate = (tableRows: Row[], index: number) => {
  return tableRows
    .sort((a, b) => new Moment(a.data[index].label, 'DD/MM/YYYY') - new Moment(b.data[index].label, 'DD/MM/YYYY'))
    .reverse();
};

const firstSortAllRows = (tableRows: Row[], isSorted: boolean = false) => {
  if (isSorted) {
    const rowsWithDate = tableRows.filter((row) => row.data[4].label !== '');
    const rowsWithOutDate = tableRows.filter((row) => row.data[4].label === '');
    const sortRowsWithDate = sortRowsByLatestDate(rowsWithDate, 4);
    return sortRowsWithDate.concat(rowsWithOutDate);
  } else {
    return tableRows;
  }
};

const List: React.FC<Props> = ({
  labels,
  icons,
  rows,
  onAction,
  listName = '',
  subtitle,
  addNewLabel,
  onAddNewItem,
  search,
  hasGap = true,
  isSmall = false,
  selectable = false,
  selectedRow,
  onRowSelect,
  filters,
  isSortable = false,
  filtersAndSearchComponents,
}: Props) => {
  const [selectedItem, setSelectedItem] = useState<Row | undefined>(undefined);
  const { isPerformed, placeholder, query, onSearch } = search || {};
  const inputClass = classNames('form__input', { hidden: !isPerformed });
  const [filterPhrase, setFilterPhrase] = useState<Nullable<string>>();
  const sortOptions = useMemo(
    () => [
      { label: 'Date submitted (newest)', value: SORT_OPTIONS.BY_DATE_SUBMITTED, option: 'Date submitted (newest)' },
      { label: 'Last updated (newest)', value: SORT_OPTIONS.BY_LAST_UPDATED, option: 'Last updated (newest)' },
    ],
    [],
  );
  const [sortOption, setSortOption] = useState<string>(sortOptions[0].value);
  const tableClassNames = {
    table: classNames('table'),
    thead: classNames('table__thead', {
      'table__thead--white table__thead--small': isSmall,
    }),
    tbody: classNames('table__tbody', {
      'table__tbody--small': isSmall,
    }),
    theadTh: 'thead__th',
  };

  const getSelectedOption = useCallback(() => {
    const found = sortOptions.find((item) => item.value === sortOption);
    return found || sortOptions[0];
  }, [sortOptions, sortOption]);

  const actionRowClickHandler = (index: number) => {
    return (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (onRowSelect) {
        const selectedRow = getRows()[index];
        setSelectedItem(selectedRow);
        onRowSelect(selectedRow);
      }
    };
  };

  const getRows = useCallback((): Row[] => {
    let item = rows;
    if (filters) {
      item = filterPhrase ? filterBy(rows, filters.filterBy, filterPhrase) : [];
    }
    if (isSortable && sortOption) {
      switch (sortOption) {
        case SORT_OPTIONS.BY_DATE_SUBMITTED: {
          return firstSortAllRows(item, true);
        }
        case SORT_OPTIONS.BY_LAST_UPDATED: {
          return sortRowsByLatestDate(item, 5);
        }
        default:
          return item;
      }
    }
    return item;
  }, [filters, rows, filterPhrase, isSortable, sortOption]);

  const handleClickFilter = (value: string) => {
    setSelectedItem(undefined);
    setFilterPhrase(value);
  };

  useEffect(() => {
    setSelectedItem(selectedRow);
  }, [selectedRow]);

  const changeSortOption = (e: any) => {
    setSortOption(e.value);
  };

  return (
    <>
      <div className="table-container list">
        {listName && (
          <div className="list__header">
            <div className="list__names">
              <h1>{listName}</h1>
              {subtitle && <p className="plum margin-top">{subtitle}</p>}
            </div>
            <div className="controls">
              {filtersAndSearchComponents}
              {addNewLabel && (
                <button type="button" onClick={onAddNewItem}>
                  {addNewLabel}
                </button>
              )}
            </div>
            {isSortable && (
              <div className="optionSelect">
                <label>Order by:</label>
                <Select
                  options={sortOptions}
                  name={'sortOption'}
                  onSelected={changeSortOption}
                  selected={getSelectedOption()}
                  disabled={false}
                  isRequired={true}
                />
              </div>
            )}
          </div>
        )}
        {filters && (
          <Field
            name=""
            component={QuoteRadio}
            options={filters.options}
            labelText={filters.text}
            clickHandler={handleClickFilter}
            selected={filterPhrase}
          />
        )}
        {onSearch && (
          <input type="search" className={inputClass} value={query} onChange={onSearch} placeholder={placeholder} />
        )}
        <table className={tableClassNames.table}>
          <thead className={tableClassNames.thead}>
            <tr className="thead__tr">
              {labels.map((label: string, index: number) => {
                return (
                  <th key={`header-label-${index}`} className={tableClassNames.theadTh}>
                    <div className="align-center">
                      {label} {icons && icons[index]}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={tableClassNames.tbody}>
            {getRows().map((row: Row, index: number) => (
              <ListRow
                key={row.key}
                items={row.data}
                onClick={onAction}
                onRowClick={actionRowClickHandler(index)}
                hasGap={hasGap}
                selectable={selectable}
                isSelected={Boolean(selectedItem && selectedItem.key === row.key)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default List;
